/**
 * App Job API Service
 *
 * API methods for managing app jobs (cron-based recurring executions).
 * App jobs define recurring execution of application functions.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from '../types/api.types';
import type {
  AppJobSanitized,
  AppJobCreateDTO,
  AppJobUpdateDTO,
  AppJobListQuery,
} from '../types/app-job.types';

// ============================================================================
// APP JOB SERVICE
// ============================================================================

export const appJobService = {
  /**
   * List all app jobs for a tenant
   */
  listAppJobs: async (
    tenantId: string,
    query?: AppJobListQuery
  ): Promise<AppJobSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<AppJobSanitized>>(
      `/tenants/${tenantId}/app-jobs`,
      { params: query }
    );
    return response.data.data;
  },

  /**
   * List app jobs for a specific application
   */
  listAppJobsByApplication: async (
    tenantId: string,
    applicationId: string,
    query?: AppJobListQuery
  ): Promise<AppJobSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<AppJobSanitized>>(
      `/tenants/${tenantId}/applications/${applicationId}/app-jobs`,
      { params: query }
    );
    return response.data.data;
  },

  /**
   * Get app job by ID
   */
  getAppJob: async (tenantId: string, appJobId: string): Promise<AppJobSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<AppJobSanitized>>(
      `/tenants/${tenantId}/app-jobs/${appJobId}`
    );
    return response.data.data;
  },

  /**
   * Create a new app job for an application
   */
  createAppJob: async (
    tenantId: string,
    applicationId: string,
    data: AppJobCreateDTO
  ): Promise<AppJobSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<AppJobSanitized>>(
      `/tenants/${tenantId}/applications/${applicationId}/app-jobs`,
      data
    );
    return response.data.data;
  },

  /**
   * Update app job
   */
  updateAppJob: async (
    tenantId: string,
    appJobId: string,
    data: AppJobUpdateDTO
  ): Promise<AppJobSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<AppJobSanitized>>(
      `/tenants/${tenantId}/app-jobs/${appJobId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete app job
   */
  deleteAppJob: async (tenantId: string, appJobId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(
      `/tenants/${tenantId}/app-jobs/${appJobId}`
    );
    return { message: response.data.message };
  },

  /**
   * Pause app job
   */
  pauseAppJob: async (tenantId: string, appJobId: string): Promise<AppJobSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<AppJobSanitized>>(
      `/tenants/${tenantId}/app-jobs/${appJobId}/pause`
    );
    return response.data.data;
  },

  /**
   * Resume app job
   */
  resumeAppJob: async (
    tenantId: string,
    appJobId: string
  ): Promise<AppJobSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<AppJobSanitized>>(
      `/tenants/${tenantId}/app-jobs/${appJobId}/resume`
    );
    return response.data.data;
  },

  // NOTE: triggerAppJob has been moved to backgroundJobService in background-job.service.ts
  // This is because triggering an app job creates a background job in the runtime API table,
  // and the Tenant API only has read-only access to the runtime API table.
  // See: backgroundJobService.triggerAppJob()
};

export default appJobService;
